// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Verification Page
// Prescription verification workflow with checklist
// ============================================================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  User,
  Pill,
  Stethoscope,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { queueApi } from '@/api/queue.api';
import { format } from 'date-fns';
import { Button, Badge, Spinner, Alert, Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/common/ui';
import toast from 'react-hot-toast';

export function VerificationPage() {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [checklist, setChecklist] = useState({
    patientVerified: false,
    drugVerified: false,
    prescriberVerified: false,
    dosageVerified: false,
    allergiesChecked: false,
    insuranceVerified: false,
  });

  const { data: rx, isLoading } = useQuery({
    queryKey: ['prescription', id],
    queryFn: () => queueApi.getPrescription(id as string),
    enabled: !!id,
  });

  const verifyMutation = useMutation({
    mutationFn: () =>
      queueApi.verifyPrescription(id as string, {
        verified: true,
        checklist,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue'] });
      queryClient.invalidateQueries({ queryKey: ['prescription', id] });
      toast.success('Prescription Verified');
      navigate({ to: '/queue' });
    },
    onError: (error: Error) => {
      toast.error(`Verification Failed: ${error.message}`);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) =>
      queueApi.rejectPrescription(id as string, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue'] });
      toast.success('Prescription Rejected');
      navigate({ to: '/queue' });
    },
  });

  const allChecked = Object.values(checklist).every(Boolean);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!rx) {
    return <Alert variant="danger">Prescription not found</Alert>;
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate({ to: '/queue' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Queue
        </Button>
        <Badge variant="warning">Pending Verification</Badge>
      </div>

      <h1 className="text-2xl font-bold">Verify Rx #{rx.rxNumber}</h1>

      {/* Warnings */}
      {rx.patient?.allergies && rx.patient.allergies.length > 0 && (
        <Alert variant="warning" title="Patient Allergies">
          {rx.patient.allergies.join(', ')}
        </Alert>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Patient Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Patient Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow label="Name" value={rx.patientName} />
            {rx.patient && (
              <>
                <InfoRow
                  label="DOB"
                  value={format(new Date(rx.patient.dateOfBirth), 'MM/dd/yyyy')}
                />
                <InfoRow label="Phone" value={rx.patient.phone} />
                <InfoRow label="Address" value={rx.patient.address?.line1 || 'N/A'} />
                {rx.patient.insurance && (
                  <>
                    <InfoRow label="Insurance" value={rx.patient.insurance.planName} />
                    <InfoRow label="Member ID" value={rx.patient.insurance.memberId} />
                  </>
                )}
              </>
            )}
          </CardContent>
          <CardFooter>
            <CheckItem
              checked={checklist.patientVerified}
              onChange={(v) => setChecklist({ ...checklist, patientVerified: v })}
              label="Patient identity verified"
            />
          </CardFooter>
        </Card>

        {/* Drug Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Pill className="h-5 w-5" />
              Drug Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow label="Drug" value={rx.drugName} />
            <InfoRow label="Strength" value={rx.strength} />
            <InfoRow label="NDC" value={rx.ndc} />
            <InfoRow label="Quantity" value={rx.quantity} />
            <InfoRow label="Days Supply" value={rx.daysSupply} />
            <InfoRow label="Sig" value={rx.sig} highlight />
            {rx.isControlled && (
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-4 w-4" />
                Controlled Substance (Schedule {rx.deaSchedule})
              </div>
            )}
          </CardContent>
          <CardFooter className="flex-col items-start gap-2">
            <CheckItem
              checked={checklist.drugVerified}
              onChange={(v) => setChecklist({ ...checklist, drugVerified: v })}
              label="Drug selection appropriate"
            />
            <CheckItem
              checked={checklist.dosageVerified}
              onChange={(v) => setChecklist({ ...checklist, dosageVerified: v })}
              label="Dosage and sig verified"
            />
          </CardFooter>
        </Card>

        {/* Prescriber Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Stethoscope className="h-5 w-5" />
              Prescriber Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow label="Name" value={rx.prescriberName} />
            {rx.prescriber && (
              <>
                <InfoRow label="NPI" value={rx.prescriber.npi} />
                <InfoRow label="DEA" value={rx.prescriber.deaNumber || 'N/A'} />
                <InfoRow label="Phone" value={rx.prescriber.phone} />
                <InfoRow label="State License" value={rx.prescriber.stateLicense} />
              </>
            )}
          </CardContent>
          <CardFooter>
            <CheckItem
              checked={checklist.prescriberVerified}
              onChange={(v) =>
                setChecklist({ ...checklist, prescriberVerified: v })
              }
              label="Prescriber credentials verified"
            />
          </CardFooter>
        </Card>

        {/* Additional Checks */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Checks</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <CheckItem
              checked={checklist.allergiesChecked}
              onChange={(v) => setChecklist({ ...checklist, allergiesChecked: v })}
              label="Checked for drug allergies"
            />
            <CheckItem
              checked={checklist.insuranceVerified}
              onChange={(v) =>
                setChecklist({ ...checklist, insuranceVerified: v })
              }
              label="Insurance eligibility verified"
            />
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-6 border-t">
        <Button
          variant="danger"
          onClick={() => {
            const reason = prompt('Enter rejection reason:');
            if (reason) rejectMutation.mutate(reason);
          }}
        >
          <XCircle className="h-4 w-4 mr-2" />
          Reject
        </Button>
        <Button
          disabled={!allChecked}
          onClick={() => verifyMutation.mutate()}
          loading={verifyMutation.isPending}
        >
          <CheckCircle2 className="h-4 w-4 mr-2" />
          Verify & Continue to DUR
        </Button>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{label}</span>
      <span
        className={`font-medium ${highlight ? 'bg-yellow-100 px-2 py-0.5 rounded' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

function CheckItem({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 rounded border-slate-300 text-pharmacy-primary focus:ring-pharmacy-primary"
      />
      <span className={checked ? 'text-slate-900' : 'text-slate-500'}>
        {label}
      </span>
      {checked && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
    </label>
  );
}
